import { CoreTypes, type ConnectorEnd, type Id, type ObjectRecord } from '@vp/shared-types';
import { commands as cmd, type CanvasDocument, type Vec2 } from '@vp/core';

/**
 * Collapsed branches (mind-map style). A branch is a connector from one shape to another; a connector
 * with `collapsed` folds away the shape it leads to and everything further along its branches, at every
 * depth, with the connector itself; the shape it leaves shows a counter instead. Other branches are untouched,
 * and a shape that a visible shape also reaches by an expanded branch stays visible.
 * Shapes that hang only on the folded part go with it whichever way their arrows point (a connector drawn
 * back towards the branch still holds its shape to it), and so does the content of folded frames and groups.
 */
export interface Folding {
  hidden: Set<Id>;
  /** Collapsed connector → how many shapes it folds away (all levels). */
  counts: Map<Id, number>;
}

export interface Branch {
  conn: Id;
  from: Id;
  child: Id;
  z: string;
  collapsed: boolean;
  /** Where the connector leaves its shape and enters the child (world). */
  start: Vec2;
  end: Vec2;
}

const isLine = (o: ObjectRecord) => o.type === CoreTypes.connector || o.type === CoreTypes.line;

/** Outgoing branches of every shape, in layer order. */
export function branchesOf(doc: CanvasDocument): Map<Id, Branch[]> {
  const out = new Map<Id, Branch[]>();
  for (const c of doc.allObjects()) {
    if (c.type !== CoreTypes.connector) continue;
    const s = c.props['start'] as ConnectorEnd | undefined;
    const e = c.props['end'] as ConnectorEnd | undefined;
    if (!s?.objectId || !e?.objectId || s.objectId === e.objectId) continue;
    const from = doc.get(s.objectId);
    const to = doc.get(e.objectId);
    if (!from || !to || isLine(from) || isLine(to)) continue;
    let list = out.get(from.id);
    if (!list) out.set(from.id, (list = []));
    list.push({ conn: c.id, from: from.id, child: to.id, z: c.z, collapsed: c.collapsed === true, start: s.point, end: e.point });
  }
  for (const list of out.values()) list.sort((a, b) => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0));
  return out;
}

/** The child of a branch and every shape reachable from it along branches. */
function reachFrom(branches: Map<Id, Branch[]>, child: Id): Set<Id> {
  const seen = new Set<Id>([child]);
  const queue = [child];
  while (queue.length) {
    for (const b of branches.get(queue.shift()!) ?? []) {
      if (seen.has(b.child)) continue;
      seen.add(b.child);
      queue.push(b.child);
    }
  }
  return seen;
}

/** Shapes joined by connectors (only expanded ones unless `all`), either direction. */
function neighbours(branches: Map<Id, Branch[]>, all = false): Map<Id, Id[]> {
  const out = new Map<Id, Id[]>();
  const link = (a: Id, b: Id) => {
    const list = out.get(a);
    if (list) list.push(b);
    else out.set(a, [b]);
  };
  for (const list of branches.values()) {
    for (const b of list) {
      if (b.collapsed && !all) continue;
      link(b.from, b.child);
      link(b.child, b.from);
    }
  }
  return out;
}

/** Shapes joined to `start` (either direction) without passing `stop` and staying inside `within` when given. */
function spread(adj: Map<Id, Id[]>, start: Id, stop: Id, within?: Set<Id>): Set<Id> {
  const seen = new Set<Id>([start]);
  const queue = [start];
  while (queue.length) {
    for (const n of adj.get(queue.shift()!) ?? []) {
      if (n === stop || seen.has(n) || (within && !within.has(n))) continue;
      seen.add(n);
      queue.push(n);
    }
  }
  return seen;
}

export function computeFolding(doc: CanvasDocument, branches = branchesOf(doc)): Folding {
  const hidden = new Set<Id>();
  const counts = new Map<Id, number>();
  const folded: Branch[] = [];
  for (const list of branches.values()) for (const b of list) if (b.collapsed) folded.push(b);
  if (!folded.length) return { hidden, counts };
  const reach = new Map(folded.map((b) => [b.conn, reachFrom(branches, b.child)]));
  for (const set of reach.values()) for (const id of set) hidden.add(id);
  // a shape that a visible shape also leads to by an expanded branch stays (and so does what hangs from it)
  const incoming = new Map<Id, Branch[]>();
  for (const list of branches.values()) for (const b of list) incoming.set(b.child, [...(incoming.get(b.child) ?? []), b]);
  for (let shrunk = true; shrunk; ) {
    shrunk = false;
    for (const id of hidden) {
      if ((incoming.get(id) ?? []).some((b) => !b.collapsed && !hidden.has(b.from))) {
        hidden.delete(id);
        shrunk = true;
      }
    }
  }
  // shapes tied to the folded part by arrows pointing the other way: they go too, unless they are still
  // joined (any direction) to a shape outside it
  const adj = neighbours(branches);
  const adjAll = neighbours(branches, true);
  const tied = new Set<Id>();
  for (const b of folded) {
    if (hidden.has(b.from) || !hidden.has(b.child)) continue;
    for (const id of spread(adjAll, b.child, b.from)) if (!hidden.has(id)) tied.add(id);
  }
  if (tied.size) {
    const anchored = new Set<Id>();
    const queue: Id[] = [];
    for (const id of adj.keys()) {
      if (hidden.has(id) || tied.has(id)) continue;
      anchored.add(id);
      queue.push(id);
    }
    while (queue.length) {
      for (const n of adj.get(queue.shift()!) ?? []) {
        if (hidden.has(n) || anchored.has(n)) continue;
        anchored.add(n);
        queue.push(n);
      }
    }
    for (const id of tied) if (!anchored.has(id)) hidden.add(id);
  }
  const shapes = new Set(hidden);
  const shown = folded.filter((b) => !hidden.has(b.from));
  for (const b of folded) hidden.add(b.conn);
  // what folded frames and groups hold
  for (const id of cmd.withContents(doc, shapes)) hidden.add(id);
  // connectors to anything hidden go too (also ones attached to hidden connectors)
  for (let grew = true; grew; ) {
    grew = false;
    for (const c of doc.allObjects()) {
      if (c.type !== CoreTypes.connector || hidden.has(c.id)) continue;
      const s = (c.props['start'] as ConnectorEnd | undefined)?.objectId;
      const e = (c.props['end'] as ConnectorEnd | undefined)?.objectId;
      if ((s && hidden.has(s)) || (e && hidden.has(e))) {
        hidden.add(c.id);
        grew = true;
      }
    }
  }
  // counters: collapsed branches whose shape is still on the canvas
  for (const b of shown) counts.set(b.conn, hidden.has(b.child) ? spread(adjAll, b.child, b.from, shapes).size : 0);
  return { hidden, counts };
}
